import Close from "@mui/icons-material/Close";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import RadioButtonChecked from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { usePendo } from "../../analytics/hooks/use-pendo-watch";
import { ProgramIcon } from "../../programs/components/program-icon";
import { useMoveCommunication } from "../hooks/use-move-communication";
import { moveDestinations, pathText } from "../move-destinations";

import type { ProgramNode, ProgramTree } from "../../program-tree/program-tree";
import type { TimelineRow } from "../program-timeline";
import type { FC } from "react";

interface MoveProps {
  row: TimelineRow;
  /** The program or folder that it is in now. */
  programId: ProgramNode["id"];
  tree: ProgramTree;
  onClose: () => void;
  /** After it moves, and after it moves back. */
  onMoved: () => void;
}

/** The dialog's content, so that the search and the choice start again each time it opens. */
const MoveForm: FC<MoveProps & ReturnType<typeof useMoveCommunication>> = (props) => {
  const { row, programId, tree, onClose, onMoved, move, moving } = props;
  const [query, setQuery] = useState("");
  const [destination, setDestination] = useState<ProgramNode | null>(null);
  const pendo = usePendo();
  const searching = query.trim() !== "";
  const rows = moveDestinations(tree, programId, query);
  // Only a choice that the search still shows can be moved to.
  const chosen = rows.find(({ node }) => node.id === destination?.id)?.node;

  const submit = async (to: ProgramNode): Promise<void> => {
    if (!(await move(row.id, to.id))) return;
    pendo?.track("program-communication-moved", {
      momentId: row.id,
      fromProgramId: programId,
      toProgramId: to.id,
    });
    onMoved();
    onClose();
    // One toast for each Communication, so that a newer move or the undo replaces it.
    const id = `move-${row.id}`;
    const undo = async (): Promise<void> => {
      if (!(await move(row.id, programId))) return;
      pendo?.track("program-communication-move-undone", { momentId: row.id, programId });
      onMoved();
      toast.success(`Moved “${row.title}” back to ${pathText(tree, programId)}.`, { id });
    };
    // Says where it went, with a button to move it back, for longer than the 2s default.
    toast.success(
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <span>{`Moved “${row.title}” to ${pathText(tree, to.id)}.`}</span>
        <Button
          size="small"
          onClick={() => void undo().catch(console.error)}
          data-analytics-id="program-timeline-move-undo"
        >
          Undo
        </Button>
      </Stack>,
      { id, duration: 10_000 },
    );
  };

  return (
    <>
      <DialogTitle id="move-title" sx={{ pr: 7 }}>
        <Typography
          variant="overline"
          sx={{ display: "block", fontWeight: 700, lineHeight: "20px", color: "text.secondary" }}
        >
          Move communication
        </Typography>
        Move “{row.title}”
      </DialogTitle>
      <IconButton
        aria-label="Close"
        onClick={onClose}
        disabled={moving}
        sx={{ position: "absolute", right: 8, top: 8 }}
        data-analytics-id="program-timeline-move-close"
      >
        <Close />
      </IconButton>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Sending isn’t affected. Who can see and edit it follows the new location.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            type="search"
            label="Find a program or folder"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <List role="radiogroup" aria-label="Destination" sx={{ py: 0 }}>
            {rows.map(({ node, depth, path, current, disabled }) => {
              const checked = destination?.id === node.id;
              return (
                <ListItemButton
                  key={node.id}
                  role="radio"
                  aria-checked={checked}
                  selected={checked}
                  disabled={disabled}
                  onClick={() => setDestination(node)}
                  sx={{ pl: searching ? 1.5 : 1.5 + depth * 3, borderRadius: "6px" }}
                  data-analytics-id="program-timeline-move-destination"
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {depth ? (
                      <FolderOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    ) : (
                      <ProgramIcon
                        data={node.icon_value}
                        fontSize="small"
                        sx={{ color: node.theme_colour }}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={node.title}
                    secondary={searching && path ? path : undefined}
                    slotProps={{
                      primary: { noWrap: true, sx: { fontWeight: depth ? undefined : 600 } },
                      secondary: { noWrap: true },
                    }}
                  />
                  {current ? (
                    <Chip size="small" label="Current location" sx={{ ml: 1, fontSize: 11 }} />
                  ) : null}
                  {checked ? (
                    <RadioButtonChecked fontSize="small" color="primary" sx={{ ml: 1.5 }} />
                  ) : (
                    <RadioButtonUnchecked fontSize="small" sx={{ ml: 1.5, color: "neutral.400" }} />
                  )}
                </ListItemButton>
              );
            })}
          </List>
          {rows.length ? null : (
            <Typography variant="body2" sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
              No programs or folders match that search.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        <Button
          onClick={onClose}
          disabled={moving}
          data-analytics-id="program-timeline-move-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          loading={moving}
          disabled={!chosen}
          onClick={() => {
            if (chosen) void submit(chosen).catch(console.error);
          }}
          data-analytics-id="program-timeline-move-confirm"
        >
          Move
        </Button>
      </DialogActions>
    </>
  );
};

/**
 * Moves a Communication to another program or folder that the caller can edit, then says where it
 * went, with a button to move it back.
 */
export const MoveDialog: FC<MoveProps & { open: boolean }> = ({ open, onClose, ...props }) => {
  const mover = useMoveCommunication();
  const close = (): void => {
    if (!mover.moving) onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="move-title">
      <MoveForm {...props} {...mover} onClose={close} />
    </Dialog>
  );
};
